/**
 * 帮助与培训中心 / Help & Training Centre — 2026-07 full rewrite.
 *
 *   • 双语：中文(zh) + English(en) —— 内容对等，同一份数据源，通过
 *     ``useLanguage()`` 切换。
 *   • 视频占位：每个章节都保留一个 <VideoSlot/>，日后录了教学视频只需在
 *     ``videoUrl`` 里填上 YouTube / Loom / Bilibili 链接即可自动内嵌。
 *   • 章节采用可折叠 <details>，方便快速扫描主题。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Package, FileText, Users, Contact, Factory, ClipboardList,
  Settings as SettingsIcon, QrCode, Share2, Mail, FileSpreadsheet,
  Filter, ShieldCheck, Sparkles, ScanLine, Star, Clock, User as UserIcon,
  Image as ImageIcon, Trash2, Copy, Tag, Layers, ArrowRight, PlayCircle, Search,
  Upload, Link as LinkIcon, Loader2, Download,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import QuoteWorkflowTutorial from "@/components/help/QuoteWorkflowTutorial";
import StepTutorial from "@/components/help/StepTutorial";
import { TUTORIALS } from "@/components/help/tutorialScripts";

// 2026-08-01 security review · replace dangerouslySetInnerHTML with a safe
// tokenizer. Splits on ``**bold**`` and ``` `code` ``` while treating all other
// text as plain strings — no HTML ever reaches the DOM parser, so a compromised
// translation file can't inject scripts.
import { SectionAdminBar, HelpNewsButton, AddSectionButton } from "@/components/help/HelpAdminTools";

function renderBullet(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <b key={i}>{part.slice(2, -2)}</b>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code key={i} className="text-xs font-mono bg-muted px-1 py-0.5 rounded">
          {part.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{part}</span>;
  });
}


const T = {
  zh: {
    title: "帮助与培训中心",
    subtitle: "宁波 · 产品资料库 · 操作手册",
    intro: "这是一份完整的操作手册：按模块讲清楚每个功能在哪里、怎么用。搜索关键词或展开章节即可查看细节，也可以一键下载 PDF 发给同事。",
    search: "搜索章节标题、关键词…",
    watchVideo: "教学视频",
    videoComingSoon: "教学视频即将上线",
    open: "展开",
    close: "收起",
    uploadVideo: "上传视频",
    uploading: "上传中…",
    pasteEmbedUrl: "或粘贴外部嵌入 URL (YouTube/Loom/Bilibili)",
    save: "保存",
    uploaded: "视频已上传",
    linked: "已保存视频链接",
    clearVideo: "清除",
    confirmClearVideo: "确认移除本章节的视频？",
  },
  en: {
    title: "Help & Training Centre",
    subtitle: "Ningbo · Product Library · Operating manual",
    intro: "A complete operating manual: what each module does, where to find it and how to use it. Search a keyword, expand a section, or download the whole thing as a PDF.",
    search: "Search section titles, keywords…",
    watchVideo: "Training video",
    videoComingSoon: "Training video coming soon",
    open: "Expand",
    close: "Collapse",
    uploadVideo: "Upload video",
    uploading: "Uploading…",
    pasteEmbedUrl: "Or paste an embed URL (YouTube/Loom/Bilibili)",
    save: "Save",
    uploaded: "Video uploaded",
    linked: "Video URL saved",
    clearVideo: "Clear",
    confirmClearVideo: "Remove the video from this section?",
  },
};

/**
 * Video slot — renders the video when a URL is available, or a friendly
 * placeholder. Admins get an inline **upload / paste-URL** editor so they
 * can attach a training video to any section without leaving the page.
 *
 *   • Upload → POSTs an MP4/WebM to ``/api/help/videos/{section}/upload``
 *     which streams to Cloudflare R2 and returns the public URL.
 *   • Paste  → PUT ``/api/help/videos/{section}`` with any embed URL
 *     (YouTube / Loom / Bilibili — the iframe just points at it).
 *   • Clear  → PUT with empty ``video_url`` deletes the mapping.
 */
const VideoSlot = ({ sectionId, videoUrl, lang, isAdmin, onChange }) => {
  const label = T[lang].watchVideo;
  const soon = T[lang].videoComingSoon;
  const [uploading, setUploading] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  const isDirectFile = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(videoUrl || "");

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/help/videos/${sectionId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(sectionId, data.video_url);
      toast.success(T[lang].uploaded);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveUrl = async () => {
    if (!urlDraft.trim()) return;
    try {
      await api.put(`/help/videos/${sectionId}`, { video_url: urlDraft.trim() });
      onChange?.(sectionId, urlDraft.trim());
      setUrlDraft("");
      toast.success(T[lang].linked);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const clear = async () => {
    if (!window.confirm(T[lang].confirmClearVideo)) return;
    try {
      await api.put(`/help/videos/${sectionId}`, { video_url: "" });
      onChange?.(sectionId, "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      {videoUrl ? (
        <div className="rounded-md overflow-hidden border border-border bg-black">
          {isDirectFile ? (
            <video src={videoUrl} controls className="w-full max-h-[420px] bg-black" data-testid={`help-video-${sectionId}`} />
          ) : (
            <div className="aspect-video">
              <iframe src={videoUrl} title={label} className="w-full h-full" allowFullScreen />
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2 bg-muted/30">
          <PlayCircle size={14} /> {label} · <em>{soon}</em>
        </div>
      )}
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid={`help-video-admin-${sectionId}`}>
          <label className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-border rounded-md cursor-pointer hover:bg-muted/50 transition-colors">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? T[lang].uploading : T[lang].uploadVideo}
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
              className="hidden"
              disabled={uploading}
              onChange={uploadFile}
              data-testid={`help-video-upload-${sectionId}`}
            />
          </label>
          <div className="inline-flex items-center gap-1">
            <LinkIcon size={12} className="text-muted-foreground" />
            <input
              type="url"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder={T[lang].pasteEmbedUrl}
              className="h-7 w-72 text-xs px-2 rounded border border-border bg-white outline-none focus:border-brand"
              data-testid={`help-video-url-${sectionId}`}
            />
            <button
              onClick={saveUrl}
              className="h-7 px-2 text-xs bg-brand text-white rounded hover:bg-brand-hover"
              data-testid={`help-video-save-${sectionId}`}
            >{T[lang].save}</button>
          </div>
          {videoUrl && (
            <button onClick={clear} className="text-xs text-destructive hover:underline" data-testid={`help-video-clear-${sectionId}`}>
              {T[lang].clearVideo}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* 章节注册表 — 每条都有 zh / en 两份内容，切换语言立即重排。
 * 这是一份**操作手册**：按「做什么 → 在哪里做 → 怎么做」组织，
 * 不记录版本变更历史。 */
const SECTIONS = (lang) => {
  const zh = lang === "zh";
  return [
    { icon: Sparkles, id: "overview",
      title: zh ? "系统总览" : "System Overview",
      bullets: zh ? [
        "本系统管理**产品资料 → 采购报价 → 客户报价单 → 导出交付**的完整链路，数据与宁波 FileMaker 双向/单向同步。",
        "左侧菜单按你的角色显示：产品、报价单、客户、工厂、采购报价、产品一览表、市场产品调查、标签管理、回收站、设置、帮助。",
        "右上角常用工具：**购物车**（切换当前活跃报价单）、**US$ / C$** 币种切换、**中 / EN** 语言切换、点自己邮箱可**修改密码**与退出。",
        "所有列表页的搜索、筛选、排序、分页都写进网址：刷新不丢、发给同事就能看到同样的结果。",
        "图片全部走 Cloudflare R2 云存储，秒开；系统自动生成 4 种尺寸供列表、报价单、邮件使用。",
      ] : [
        "The system covers the full chain: **product master → procurement quotes → customer quotations → exports**, synced with the Ningbo FileMaker database.",
        "The sidebar shows only what your role can reach: Products, Quotes, Customers, Factories, Procurement Quotes, All Product Photos, Market Survey, Tags, Trash, Settings, Help.",
        "Header tools: **Cart** (switch the active quote), **US$ / C$** currency toggle, **中 / EN** language toggle, and your email menu for password change / logout.",
        "Search, filters, sorting and pagination live in the URL — refresh keeps the state and links are shareable.",
        "All images are served from Cloudflare R2; four sizes are generated automatically for lists, quotes and emails.",
      ],
    },
    { icon: ArrowRight, id: "first-day",
      title: zh ? "第一次使用：五步上手" : "First Day: Five Steps",
      bullets: zh ? [
        "**1 登录** — 用公司邮箱和管理员发给你的密码登录，进去后先点右上角邮箱 → 修改密码。",
        "**2 找产品** — 进「产品」页，用顶部搜索框输入货号或关键词；也可以用「我的产品」只看归属自己的产品。",
        "**3 建报价单** — 「报价单」页点新建，选客户、填报价汇率，然后回产品页勾选产品点「加入报价单」。",
        "**4 核对价格** — 在报价单里点产品行的编辑图标，逐个尺寸核对成本、装箱数据与售价。",
        "**5 交付客户** — 报价单页面导出 Excel / PDF / 标签，或直接发 HTML 邮件给客户。",
      ] : [
        "**1 Sign in** — use your company email and the password from your admin, then open the email menu → Change Password.",
        "**2 Find products** — go to Products and search by item number or keyword; use **My Products** to see only what belongs to you.",
        "**3 Create a quote** — on Quotes click New, pick the customer, set the quote exchange rate, then add products from the Products page.",
        "**4 Check prices** — open each product row in the quote editor and review cost, carton data and selling price per size.",
        "**5 Deliver** — export Excel / PDF / labels from the quote page, or email the HTML quote straight to the customer.",
      ],
    },
    { icon: Users, id: "roles",
      title: zh ? "角色与权限" : "Roles & Permissions",
      bullets: zh ? [
        "**管理 (admin)** — 全部权限：任何产品、报价、用户、设置与同步操作。",
        "**外销 (export) / GT 外销 (gt_export)** — 管理「外销 = 自己」的产品、客户与报价单；新建产品时外销自动填自己。",
        "**采购 (buyer)** — 管理工厂与「采购 = 自己」的产品，负责填写采购报价。",
        "**采购经理 (procurement_manager)** — 可查看并编辑全部采购报价，用于复核与汇总。",
        "**销售经理 (sales_manager)** — 通过用户档案的「上级」字段带团队：能看能改下属的产品与报价，不能删除。",
        "**产品经理 (production_manager)** — 只进产品页，可维护图片、描述、规格、材质、卖点、分类等资料字段，价格类字段只读。",
        "**工厂 (factory)** — 只看自己相关产品，可上传自家产品图片。",
        "**客户 (customer)** — 只看分配给自己的产品与报价单，卡片式浏览。",
      ] : [
        "**admin** — full access to every product, quote, user, setting and sync action.",
        "**export / gt_export** — manages products, customers and quotes where 外销 = self; new products default the seller to you.",
        "**buyer** — manages factories and products where 采购 = self, and fills in procurement quotes.",
        "**procurement_manager** — can view and edit all procurement quotes for review and consolidation.",
        "**sales_manager** — leads a team via the Manager field on each user: can view and edit subordinates' products and quotes, but not delete.",
        "**production_manager** — Products page only: maintains photos, descriptions, specs, material, selling points and categories; price fields are read-only.",
        "**factory** — sees only its own products and can upload product photos.",
        "**customer** — sees only the products and quotes assigned to them, in card view.",
      ],
    },
    { icon: Package, id: "products",
      title: zh ? "产品资料" : "Product Master",
      bullets: zh ? [
        "**列表视图 / 网格视图**可切换。网格卡片图片上方显示产品类型徽章，右上角金色星标是收藏。",
        "顶部筛选：产品类型、类别、子类别、品牌、创建日期、工厂成本区间、销售价区间（金额跟随当前币种）。",
        "工具条：**我的收藏**、**最近查看**、**我的产品**、**显示全部**；勾选状态跨页保留 24 小时，可一键清空。",
        "**新建产品**：点顶部 + 号，货号最多 15 位；填完基本信息后再维护价格表。",
        "**编辑页**分左右两栏：左侧是图片与基础资料，右侧是价格、装箱、归属等字段；⬅➡ 按当前筛选结果翻页。",
        "价格表按尺寸一行行维护：工厂成本、关税、退税、装箱率、箱规长宽高、毛重、40HQ 装柜量，售价由公式自动算出。",
      ] : [
        "Switch between **list** and **grid** view. Grid cards show a product-type badge above the photo and a gold favourite star.",
        "Filters: product type, category, subcategory, brand, creation date, factory-cost range, selling-price range (amounts follow the active currency).",
        "Toolbar: **Favourites**, **Recently viewed**, **My products**, **Show all**. Selections persist across pages for 24 hours and can be cleared in one click.",
        "**New product**: the + button at the top; item number up to 15 characters. Fill the basics first, then the price list.",
        "**Edit page** has two columns: images and basic data on the left, pricing / carton / ownership on the right. ⬅➡ walks the current filtered result set.",
        "The price list is maintained per size: factory cost, duty, tax rebate, packing rate, carton L/W/H, gross weight and 40HQ quantity; selling prices are computed by formula.",
      ],
    },
    { icon: ImageIcon, id: "photos",
      title: zh ? "图片、抠图与合成图" : "Photos, Cutout & Composites",
      bullets: zh ? [
        "每个产品可以有**产品图**、**花型图**，系统能把两张并排合成一张**合成图**供报价单使用。",
        "上传后自动生成 4 种尺寸（邮件 240×180、小 120、中 360、大 800）并推到云存储，列表只加载小图。",
        "**AI 抠图**：编辑页图片旁点抠图按钮，自动去掉背景换成纯白底，约 5 秒完成。只对在本系统上传的图片自动处理，不会覆盖 FileMaker 同步过来的原图。",
        "「产品一览表」页汇总全部产品照片，可按货号搜索，用于选图和比对。",
        "点任意图片可放大查看；图片超过 2MB 会自动压缩，保持清晰度的同时加快加载。",
      ] : [
        "Each product can carry a **main photo** and a **pattern photo**; the system can merge them side-by-side into a **composite** used by quotes.",
        "Uploads generate four sizes automatically (mail 240×180, sm 120, md 360, lg 800) and push them to cloud storage; lists load only the small ones.",
        "**AI cutout**: click the cutout button next to any photo on the edit page to drop the background onto pure white — about 5 seconds. It only runs automatically on photos uploaded here, never on FileMaker-synced originals.",
        "The **All Product Photos** page collects every photo for searching and visual comparison.",
        "Click any image to enlarge. Files over 2 MB are compressed automatically to keep loading fast.",
      ],
    },
    { icon: FileText, id: "quotes",
      title: zh ? "客户报价单" : "Customer Quotations",
      bullets: zh ? [
        "**新建报价单**：填报价单号、选客户（下拉来自客户管理）、项目名、**报价汇率**。汇率决定美元价 = 人民币价 ÷ 汇率。",
        "**加产品**：把报价单设为活跃（右上角购物车），在产品页勾选后点「加入报价单」；也可以在报价单里直接搜索添加。",
        "**编辑产品行**：点报价单号左边的编辑图标改单头信息；点产品行图标进入明细编辑，逐尺寸调价、改装箱数据、导入订单资料。",
        "**列可自定义**：产品表头支持拖动排序，顺序记在本机，下次打开还是你排的样子。",
        "**共享**：把同事邮箱加入共享名单，可分为只读或可编辑；销售经理默认能看到全组报价单。",
        "**导出**：Excel（多种模板）、PDF、打印标签、HTML 邮件（Resend 发送）。导出时价格会被锁定留档，之后成本变动不影响已发出的报价。",
      ] : [
        "**New quote**: enter the quote number, pick the customer (from Customers), the project and the **quote exchange rate** — USD price = RMB price ÷ rate.",
        "**Add products**: set the quote active via the header cart, tick products on the Products page and click Add to quote; you can also search and add from inside the quote.",
        "**Edit rows**: the pencil left of the quote number edits the header; the row icon opens the item editor for per-size pricing, carton data and order-data import.",
        "**Custom columns**: drag the item table headers to reorder; the order is remembered on your machine.",
        "**Sharing**: add teammate emails as read-only or editable; sales managers see the whole team's quotes by default.",
        "**Exports**: Excel (several templates), PDF, printed labels, HTML email via Resend. Prices are frozen on export so later cost changes don't alter what you already sent.",
      ],
    },
    { icon: ClipboardList, id: "procurement",
      title: zh ? "采购报价" : "Procurement Quotes",
      bullets: zh ? [
        "采购登录后默认只看自己的记录；在搜索框输入货号、名称、工厂或**报价客户**会跨全库搜索。",
        "**新建 / 编辑窗体**分三块：基本信息（货号、名称、工厂、尺寸、规格）、价格信息（每米价、用料尺寸、加工费、辅料、运费、利润税率 → 自动算出价格）、装箱数据（装箱率、长宽高、QTY HQ、毛重）。",
        "**QTY HQ** 会按箱规和装箱率自动算出，也可以手动改。",
        "**上一个 / 下一个**按钮在窗体内连续处理多条记录；**复制为新记录**保留内容并把日期设为今天，窗体停留在新记录上继续编辑。",
        "**修改历史**：操作列的历史图标可以看到同一货号的所有版本（含其他同事的记录），每次编辑前的快照都留档。",
        "列表按最近修改时间倒序，刚编辑过的记录排在最上面。",
      ] : [
        "Buyers see only their own records by default; typing an item number, name, factory or **quote customer** in the search box searches the whole database.",
        "The **editor** has three blocks: basics (item, name, factory, size, spec), pricing (metre price, fabric size, labour, trims, freight, margin & tax → computed price) and carton data (packing rate, L/W/H, QTY HQ, gross weight).",
        "**QTY HQ** is calculated from the carton dimensions and packing rate, and can still be overridden.",
        "**Prev / Next** walks records without closing the form; **Save as copy** duplicates the record with today's date and keeps the form on the new one.",
        "**Change history**: the history icon shows every version of that item number across colleagues, with a snapshot taken before each edit.",
        "The list is sorted by most-recent change, so what you just edited stays on top.",
      ],
    },
    { icon: Download, id: "order-products",
      title: zh ? "订单产品资料（合同装箱数据）" : "Order Product Data (Contract Cartons)",
      bullets: zh ? [
        "这是从宁波合同订单库**单向同步**过来的历史合同数据，只读，用来快速复用装箱数据和对比价格。",
        "**在哪里用**：采购报价编辑窗体的「订单产品资料」面板、产品价格表每行的箱子图标、报价单产品行的箱子图标。",
        "**自动匹配**：系统按当前货号自动找出历史合同记录，先精确匹配，再忽略大小写和分隔符，最后去掉版本后缀（如 `NL17717-1` → `NL17717`）。",
        "**一键导入**：点某行的「导入」把装箱率、长、宽、高、毛重、40HQ 装柜量填进当前记录；在价格表和报价单里还可以勾选「同时把采购价导入工厂成本」。",
        "**历史最低价**：面板顶部绿色条直接显示该产品历史最低采购价、对应工厂、合同号和日期，右侧可一键导入那一笔。",
        "**弹窗搜索**：可搜中文名称 / 英文名称 / 产品编号 / 合同号 / 厂家，并按合同号、客户、厂家筛选（都能手动输入并自动提示）。采购价旁的红▲绿▼显示与当前成本的涨跌幅。",
        "同步时间：每天美东时间早上 07:00 自动跑一次；管理员也可在设置页点「立即同步」，并可修改同步起始日期。",
      ] : [
        "Read-only historical contract data pulled **one-way** from the Ningbo contract database, used to reuse carton data and compare prices.",
        "**Where to use it**: the Order Product Data panel in the procurement quote editor, the carton icon on each product price row, and the carton icon on each quote item row.",
        "**Auto matching** by item number: exact first, then case/separator-insensitive, then with the version suffix stripped (e.g. `NL17717-1` → `NL17717`).",
        "**One-click import** fills packing rate, L/W/H, gross weight and 40HQ quantity. On price rows and quote rows you can also tick *also import the purchase price into factory cost*.",
        "**Lowest ever price**: a green bar shows the cheapest historical purchase price with its factory, contract number and date, plus a one-click import.",
        "**Dialog search** covers Chinese name / English name / item number / contract number / factory, with typeable contract, customer and factory filters. Red ▲ / green ▼ next to each price shows the change against your current cost.",
        "Sync runs daily at 07:00 America/New_York; admins can also press Sync now in Settings and change the start date.",
      ],
    },
    { icon: FileSpreadsheet, id: "excel",
      title: zh ? "Excel 导入" : "Excel Import",
      bullets: zh ? [
        "产品页点**导入 Excel** → 上传文件 → 通用格式需要做一次列映射；宁波 WISE / Better Home 标准格式可直接预览。",
        "标准格式支持多个 Sheet（如 BEDDING / BLANKET / BATHROOM）自动合并，常见表头（QTY/CTN、CBM/CTN、40'HQ qty）自动识别。",
        "货号重复时自动加后缀 `-2 / -3`，不会覆盖也不会跳过，结果页用蓝色标签标出「重命名自 XXX」。",
        "某一列填图片网址时，导入过程会自动下载图片并存入云存储。",
        "导入完成后点「查看导入的产品」，产品页只显示这批刚导入的记录，方便立刻补资料。",
      ] : [
        "Products → **Import Excel** → upload. The universal format needs a one-time column mapping; Ningbo WISE / Better Home standard files go straight to preview.",
        "Standard files merge multiple sheets (BEDDING / BLANKET / BATHROOM) automatically and resolve common headers (QTY/CTN, CBM/CTN, 40'HQ qty).",
        "Duplicate item numbers get a `-2 / -3` suffix instead of being skipped or overwritten; the result page flags them as *renamed from XXX*.",
        "If a column holds an image URL, the picture is downloaded and archived to cloud storage during import.",
        "After importing, click **View imported products** to see just that batch and finish the data entry.",
      ],
    },
    { icon: Tag, id: "tags",
      title: zh ? "标签管理" : "Tag Management",
      bullets: zh ? [
        "标签用来给产品分组：客户专属、季节、展会、项目等，一个产品可以有多个标签。",
        "「标签管理」页左侧是标签列表（按创建时间从新到旧，显示创建人），右侧是该标签下的产品。",
        "右侧可切换**我的产品**（只看归属自己的）与全部产品；如果你名下没有产品，会自动显示全部并给出提示。",
        "选中产品后可以批量打标签，或一键**加入当前活跃报价单**。",
        "标签可以共享给同事，被共享的人也能在自己的标签列表里看到。",
      ] : [
        "Tags group products by customer, season, fair, project… and a product can carry several.",
        "The Tags page lists tags on the left (newest first, with creator) and that tag's products on the right.",
        "Toggle between **My products** and all products; if nothing is assigned to you, everything is shown with a short note.",
        "Select products to tag them in bulk, or push them straight into the **active quote**.",
        "Tags can be shared with colleagues, who then see them in their own tag list.",
      ],
    },
    { icon: Contact, id: "customers-factories",
      title: zh ? "客户与工厂" : "Customers & Factories",
      bullets: zh ? [
        "**客户**字段：名称、货币、价格系数、海运费、归属外销。报价单的价格计算会用到这些设定。",
        "外销只能维护自己的客户；销售经理可维护全组客户；管理员不受限。",
        "**工厂**由采购维护，每家工厂可以看到关联的产品清单，方便询价和跟单。",
        "客户和工厂都支持搜索与分页，删除前会提示是否有关联数据。",
      ] : [
        "**Customer** fields: name, currency, price multiplier, freight and the owning salesperson — all feed the quote pricing.",
        "Exports maintain their own customers, sales managers the whole team's, admins everything.",
        "**Factories** are maintained by buyers; each one lists its linked products for enquiries and follow-up.",
        "Both pages support search and pagination, and warn about linked data before deletion.",
      ],
    },
    { icon: ScanLine, id: "market-survey",
      title: zh ? "市场产品调查" : "Market Survey",
      bullets: zh ? [
        "用于收集卖场、展会现场拍摄的竞品照片与信息，按店名归类。",
        "支持一次批量上传大量照片（最多 300 张），同一记录的多张照片会合并成一张卡片，右上角显示 +N。",
        "可以按店名、关键词搜索；照片点放大镜查看大图。",
        "看中的产品可以批量导入到正式产品库，直接进入产品资料流程。",
      ] : [
        "Collects competitor photos and notes captured in stores and at fairs, grouped by store name.",
        "Batch-upload up to 300 photos at once; multiple photos of the same record collapse into one card with a +N badge.",
        "Search by store or keyword; click the magnifier on any photo for the lightbox.",
        "Promising items can be batch-imported into the main product library.",
      ],
    },
    { icon: QrCode, id: "qr-share",
      title: zh ? "扫码与分享链接" : "QR Codes & Share Links",
      bullets: zh ? [
        "每个产品都有唯一二维码（打印在标签上）。扫码后按扫描者的角色显示不同内容：客户看图片与售价，工厂看生产信息。",
        "**采购报价链接**：在报价单里为某个采购生成链接，采购打开后登录即可**直接进入**要填报的页面，只看到分配给自己的产品。",
        "链接有有效期，可以随时作废重新生成。",
        "报价单也可以直接共享给同事（只读或可编辑），不需要发文件。",
      ] : [
        "Every product has a unique QR code printed on its label. Scanning shows a role-specific view: customers see photos and prices, factories see production details.",
        "**Procurement links**: generate a link for a buyer from a quote; after logging in they land **straight** on the page they need, showing only their assigned products.",
        "Links expire and can be revoked and regenerated at any time.",
        "Quotes can also be shared with colleagues (read-only or editable) without sending files.",
      ],
    },
    { icon: Layers, id: "sync",
      title: zh ? "数据同步" : "Data Synchronisation",
      bullets: zh ? [
        "**产品资料同步（双向）**：本系统拥有描述（中/英）、产品图片、标签，会写回 FileMaker；其他字段以 FileMaker 为准。",
        "**采购报价同步（双向）**：在本系统新建的采购报价会推送回 FileMaker，FileMaker 侧的修改也会同步过来。",
        "**合同订单产品（单向）**：只从 FileMaker 读取，永不写回；产品图片不同步。",
        "同步时间：产品资料每天增量 + 全量各一次；合同订单产品每天美东时间早上 07:00。管理员可在设置页手动触发并查看进度。",
        "注意：在 FileMaker 上**新增或改名字段**后，需要重跑一次全量同步，历史记录才会补齐。",
      ] : [
        "**Product master (two-way)**: this system owns descriptions (zh/en), photos and tags and pushes them back to FileMaker; FileMaker owns the rest.",
        "**Procurement quotes (two-way)**: records created here are pushed to FileMaker, and FileMaker edits flow back.",
        "**Contract order products (one-way)**: read from FileMaker only, never written back, and photos are not synced.",
        "Schedule: product master runs an incremental and a full pass daily; contract order products run at 07:00 America/New_York. Admins can trigger any sync manually and watch progress.",
        "Note: after **adding or renaming a field in FileMaker**, run one full sync so historical records are back-filled.",
      ],
    },
    { icon: SettingsIcon, id: "settings",
      title: zh ? "系统设置（管理员）" : "System Settings (Admin)",
      bullets: zh ? [
        "**价格公式**：美元利润率、加元利润率、汇率、海运费、默认关税与退税率 —— 改动会影响所有未锁价的计算结果。",
        "**产品类型管理**：4 个默认类型（库存 / Low MOQ / 标准 / 样本）可改名或停用但不可删除，自定义类型可增删排序。",
        "**字典维护**：类别、子类别、品牌、标签。",
        "**同步卡片**：FileMaker 产品同步、采购报价同步、合同订单产品同步（可改起始日期），都能看进度与记录数。",
        "**用户管理**：新建账号、指定角色、设置上级、重置密码、停用账号。",
        "**系统模板**：报价邮件正文、标签打印布局等预设文案，可在线编辑。",
      ] : [
        "**Pricing formulas**: USD margin, CAD margin, exchange rate, freight, default duty and tax rebate — changes affect every unlocked calculation.",
        "**Product types**: the four defaults (Stock / Low MOQ / Standard / Sample) can be renamed or disabled but not deleted; custom types can be added, sorted and removed.",
        "**Dictionaries**: category, subcategory, brand, tag.",
        "**Sync cards**: FileMaker product sync, procurement quote sync and contract order sync (with an editable start date), each showing progress and record counts.",
        "**User management**: create accounts, assign roles and managers, reset passwords, deactivate users.",
        "**System templates**: quote email bodies, label layouts and other reusable content, editable inline.",
      ],
    },
    { icon: Trash2, id: "trash",
      title: zh ? "回收站" : "Recycle Bin",
      bullets: zh ? [
        "删除的产品先进回收站，不会立即消失，**30 天后自动清除**。",
        "在回收站可以搜索、恢复，或立即彻底删除。",
        "删除产品价格表里的所有尺寸行**不会**删除产品本身，产品仍然保留在列表里。",
      ] : [
        "Deleted products land in the Recycle Bin instead of disappearing, and are purged automatically **after 30 days**.",
        "From there you can search, restore, or delete permanently.",
        "Removing every size row from a product's price list does **not** delete the product itself.",
      ],
    },
    { icon: ShieldCheck, id: "account",
      title: zh ? "账号与密码" : "Account & Password",
      bullets: zh ? [
        "**修改密码**：登录后点右上角自己的邮箱 → 修改密码 → 输入当前密码 + 新密码（至少 6 位）+ 确认。",
        "忘记密码请联系管理员，在用户管理页帮你重置。",
        "登录页右上角可以不登录就切换**中 / EN** 界面语言。",
        "浏览器建议用 Chrome 或 Edge 最新版本；手机端所有页面都做了自适应，卡片式浏览。",
      ] : [
        "**Change password**: click your email in the header → Change Password → current password + new password (min 6 chars) + confirm.",
        "Forgot your password? Ask an admin to reset it from User Management.",
        "The login page has a **中 / EN** toggle that works before signing in.",
        "Use an up-to-date Chrome or Edge. Every page is mobile-responsive with card layouts.",
      ],
    },
    { icon: Search, id: "faq",
      title: zh ? "常见问题" : "FAQ",
      bullets: zh ? [
        "**找不到某个产品？** 先按「显示全部」清掉「我的产品 / 收藏」等筛选，再用货号搜索；也检查一下是否在回收站里。",
        "**报价单价格不对？** 检查报价单头的**报价汇率**、客户的价格系数与海运费，以及该产品价格行的工厂成本和关税。",
        "**改了价格但导出的报价没变？** 导出时价格会锁定留档；需要用新价格请重新生成报价单导出。",
        "**图片显示不出来？** 少数从 FileMaker 同步过来的老图片格式较旧（如 WMF）无法在网页显示，重新上传一张即可。",
        "**订单产品资料里没有我的产品？** 该库只含合同历史记录；如果 FileMaker 刚加了字段或新合同，请让管理员在设置页点一次「立即同步」。",
        "**页面数据像是旧的？** 按 F5 刷新即可；列表状态都在网址里，不会因刷新丢失。",
      ] : [
        "**Can't find a product?** Click Show all to clear the My-products / Favourites filters, search by item number, and check the Recycle Bin.",
        "**Quote price looks wrong?** Check the quote's **exchange rate**, the customer's multiplier and freight, and the factory cost / duty on that price row.",
        "**Changed a price but the export didn't change?** Prices are frozen at export time — re-export the quote to pick up new prices.",
        "**An image won't display?** A few legacy FileMaker photos use old formats (e.g. WMF) that browsers can't render; re-upload the picture.",
        "**A product is missing from Order Product Data?** That database only holds contract history — after a FileMaker field change or a new contract, ask an admin to press Sync now.",
        "**Data looks stale?** Just refresh; list state lives in the URL and survives reloads.",
      ],
    },
  ];
};

/* ==================================================================== */
export default function HelpCenter() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [query, setQuery] = useState("");
  const [videos, setVideos] = useState({});
  const [pdfBusy, setPdfBusy] = useState(false);
  // 2026-06 · Admin-editable help content (overrides / hidden flags / custom
  // sections / 最新更新), loaded per UI language.
  const [content, setContent] = useState({ overrides: {}, custom: [], news: { items: [], unread: 0 } });
  const loadContent = useCallback(async () => {
    try {
      const { data } = await api.get("/help/content", { params: { lang } });
      setContent(data);
    } catch { /* defaults stay */ }
  }, [lang]);
  useEffect(() => { loadContent(); }, [loadContent]);
  const sections = useMemo(() => {
    const merged = SECTIONS(lang).map((s) => {
      const ov = content.overrides?.[s.id];
      return ov ? { ...s, title: ov.title || s.title, bullets: ov.bullets?.length ? ov.bullets : s.bullets, hidden: ov.hidden } : s;
    });
    const extra = (content.custom || []).map((c) => ({
      icon: Sparkles, id: `custom-${c.id}`, customId: c.id,
      title: c.title, bullets: c.bullets || [], hidden: c.hidden,
    }));
    const all = [...merged, ...extra];
    return isAdmin ? all : all.filter((s) => !s.hidden);
  }, [lang, content, isAdmin]);

  // 2026-02 · Download-as-PDF button. Posts the currently-localised sections
  // to /api/help/export-pdf, which renders them via reportlab and streams the
  // file back. We build the sections payload from the SAME `sections` array
  // the page shows so the PDF always matches what the user sees.
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const payload = {
        title: T[lang].title,
        subtitle: T[lang].subtitle,
        intro: T[lang].intro,
        sections: sections.map((s) => ({ id: s.id, title: s.title, bullets: s.bullets })),
      };
      const res = await api.post("/help/export-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `help-center-${lang}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("PDF generation failed. Check backend logs.");
    } finally {
      setPdfBusy(false);
    }
  };

  // Fetch the section → video-url map once. Refreshes when an admin uploads.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/help/videos");
        setVideos(data || {});
      } catch { /* no-op — placeholders will show */ }
    })();
  }, []);
  const handleVideoChange = (sectionId, url) => {
    setVideos((cur) => ({ ...cur, [sectionId]: url }));
  };

  // 动画演示教程 — 挂在对应章节（客户报价单 / 采购报价 / 扫码分享 / 产品资料）
  const tutorials = useMemo(() => TUTORIALS(lang), [lang]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) =>
      s.title.toLowerCase().includes(q) ||
      s.bullets.some((b) => b.toLowerCase().includes(q)),
    );
  }, [sections, query]);

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-16 animate-fade-in-up" data-testid="help-center">
      <section className="rounded-md border border-border bg-gradient-to-br from-brand/8 via-white to-white p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-brand text-white flex items-center justify-center shrink-0">
            <FileText size={22} />
          </div>
          <div>
            <div className="flex items-start justify-between gap-3">
              <h1 className="font-heading text-3xl font-bold tracking-tight">{T[lang].title}</h1>
              <div className="flex items-center gap-2 shrink-0">
                {/* 2026-06 · 最新更新 button — dev-maintained changelog with unread dot */}
                <HelpNewsButton news={content.news} lang={lang} onSeen={loadContent} />
                {/* 2026-02 · One-click PDF export of the whole help centre. */}
                <button
                  type="button"
                  onClick={downloadPdf}
                  disabled={pdfBusy}
                  data-testid="help-download-pdf"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-brand bg-brand text-white text-xs font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60 shrink-0"
                >
                  {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                  {lang === "zh" ? "下载 PDF" : "Download PDF"}
                </button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">{T[lang].subtitle}</p>
            <p className="text-sm mt-3 leading-relaxed max-w-2xl">{T[lang].intro}</p>
            <div className="mt-4 relative max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder={T[lang].search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full h-9 pl-9 pr-3 rounded-md border border-border bg-white text-sm outline-none focus:border-brand"
                data-testid="help-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 2026-02 · Interactive step-by-step tutorial for the "外销 filter →
       * generate quote" workflow. Placed prominently above the section list
       * because it's the #1 daily task and the most-asked-about training topic. */}

      <QuoteWorkflowTutorial lang={lang} />

      {isAdmin && (
        <div className="flex justify-end"><AddSectionButton lang={lang} onSaved={loadContent} /></div>
      )}

      {filtered.map((section) => {
        const Icon = section.icon;
        return (
          <details
            key={section.id}
            open
            className="rounded-md border border-border bg-card overflow-hidden group"
            data-testid={`help-section-${section.id}`}
          >
            <summary className="cursor-pointer list-none px-5 py-3 flex items-center gap-3 hover:bg-muted/40 transition-colors">
              <Icon size={16} className="text-brand shrink-0" />
              <span className="font-medium">{section.title}</span>
              {isAdmin && <SectionAdminBar section={section} lang={lang} onSaved={loadContent} />}
              <ArrowRight size={14} className={`text-muted-foreground transition-transform group-open:rotate-90 ${isAdmin ? "" : "ml-auto"}`} />
            </summary>
            <div className="px-5 pb-5 pt-1 text-sm leading-relaxed">
              <ul className="list-disc list-outside pl-5 space-y-1.5">
                {section.bullets.map((b, i) => (
                  <li key={`${section.id}-b-${i}`}>{renderBullet(b)}</li>
                ))}
              </ul>
              {tutorials[section.id] && (
                <StepTutorial script={{ ...tutorials[section.id], id: section.id }} lang={lang} />
              )}
              <VideoSlot
                sectionId={section.id}
                videoUrl={videos[section.id] || section.videoUrl}
                lang={lang}
                isAdmin={isAdmin}
                onChange={handleVideoChange}
              />
            </div>
          </details>
        );
      })}
    </div>
  );
}
